<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title" />
    <div v-if="isLoading" class="loader-container">
      <Loader :loading="isLoading" size="sm"></Loader>
    </div>
    <div class="orders-add-edit-container" v-else>
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />

      <OrdersRightPanel :value="formData" :saving="saving" @save="save" @back="gotoList" />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import settings from "./partials/settings.vue";
import products from "./partials/products.vue";
import additionalData from "./partials/additionalData.vue";
import OrdersRightPanel from "./partials/OrdersRightPanel.vue";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import { successMessage, errorMessage, handlePromiseException } from "@/helpers/common";
import Loader from "@/components/Loader";
import moment from "moment";

export default {
  name: "OrdersAddOrEdit",
  data() {
    return {
      title: this.$t("stock_management.orders.edit"),
      isLoading: false,
      formData: {
        uuid: null,
        order_type_id: null,
        order_type: "",
        legal_entity_uuid: null,
        legal_entity_name: "",
        order_number: "",
        partner_uuid: null,
        partner_name: "",
        date: null,
        delivery_date: null,
        products: [],
        notes: null,
      },
      steps: [
        {
          number: 1,
          component: settings,
          label: this.$t("common.settings"),
          ref: "OrdersSettings",
        },
        {
          number: 2,
          component: products,
          label: this.$t("common.product"),
          ref: "OrdersProducts",
        },
        {
          number: 3,
          component: additionalData,
          label: this.$t("common.additional_data"),
          ref: "OrdersAdditionalData",
        },
      ],
      currentStep: 1,
      saving: false,
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_ORDERS,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  components: {
    settings,
    products,
    additionalData,
    OrdersRightPanel,
    Forbidden,
    EditCreateTitleBox,
    StepsPanelWrapper,
    Loader,
  },
  methods: {
    close() {},
    ...mapActions({
      createSmOrder: "smOrders/createSmOrder",
      updateSmOrder: "smOrders/updateSmOrder",
      getSmOrderByUuid: "smOrders/getSmOrderByUuid",
    }),
    handleAddSave(dataToSave) {
      this.createSmOrder(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "orders" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleEditSave(dataToSave) {
      this.updateSmOrder(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "orders" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async fetchSmOrderData(uuid) {
      try {
        this.getSmOrderByUuid({
          uuid: uuid,
        }).then((response) => {
          if (response && response.data) {
            response.data.date = moment(response.data.date).format(this.getDefaultDateFormat);
            if (response.data.delivery_date && response.data.delivery_date !== "eet") {
              response.data.delivery_date = moment(response.data.delivery_date).format(this.getDefaultDateFormat);
            }

            this.formData = { ...this.formData, ...response.data };

            for (const step of this.steps) {
              const stepComponent = this.$refs?.[step.ref];
              if (stepComponent && typeof stepComponent.updateData === "function") {
                if (step.number === 1) {
                  stepComponent.updateData(response.data);
                } else {
                  setTimeout(() => {
                    stepComponent.updateData(response.data);
                  }, step.number * 10);
                }
              }
            }
          }
        });
      } finally {
        this.isLoading = false;
      }
    },
    getStepProps() {
      return {
        formData: this.formData,
      };
    },
    onStepInput(step, payload) {
      if (!step) return;
      this.formData = { ...this.formData, ...(payload || {}) };
    },
    handleValidationError(error) {
      errorMessage(this, error.message || this.$t("fill_out_error"));
    },
    save() {
      this.validateStepsAndSave();
    },
    async validateStepsAndSave() {
      const wrapper = this.$refs?.stepsWrapper;
      if (wrapper && typeof wrapper.validateSteps === "function") {
        const errors = await Promise.resolve(wrapper.validateSteps());
        if (errors && errors.length) {
          errors.forEach((msg) => errorMessage(this, msg));
          return;
        }
      }

      this.saving = true;
      const dataToSave = {
        ...this.formData,
      };

      if (dataToSave.uuid) {
        this.handleEditSave(dataToSave);
      } else {
        this.handleAddSave(dataToSave);
      }
    },
    gotoList() {
      this.$router.push({ name: "orders" });
    },
    roundToDecimals(value, decimals) {
      if (typeof value !== "number") value = Number(value) || 0;
      return Number(value.toFixed(decimals));
    },
  },
  mounted() {
    if (this.$route.params.uuid && this.$route.params.uuid !== "add") {
      this.formData.uuid = this.$route.params.uuid;
      this.title = this.$t("stock_management.orders.edit");
      this.isLoading = true;
      setTimeout(() => {
        this.fetchSmOrderData(this.$route.params.uuid);
      }, 500);
    } else {
      this.formData.uuid = null;
      this.title = this.$t("stock_management.orders.create_new");
    }
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.orders-add-edit-container {
  display: flex;
  height: 100%;
  width: 100%;
}
</style>
